<!-- eslint-disable vue/html-indent -->
<!-- This component represents the home page for the Health Care Entity (HCE) portal. -->
<template>
  <div>
    <MnLoader :visible="pageState.state.loading" />
    <ErrorMessage :visible="pageState.state.error" :errorMessage="pageState.state.errorMessage" />
    <CardSection class="mb-3">
      <template #title>
        <div class="d-flex align-items-center mb-2">
          <!-- Title section for the user's profile with an edit button. -->
          <h2 class="fs-3">MY PROFILE</h2>
          <PvButton
            label="Edit Profile"
            :icon="ICONS.Edit"
            arial-label="Edit profile"
            class="ms-5"
            size="small"
            title="Edit your profile details."
            rounded outlined
            @click="() => contactModalVisible = true"
          />
        </div>
      </template>
      <template #content>
        <div class="p-2">
          <!-- Display user's first and last name in a read-only format. -->
          <MnRow>
            <MnColumn cols="2">
              <ReadOnlyText label="First Name" :value="txStore.contact?.firstName" :showLoading="pageState.state.loading" />
            </MnColumn>
            <MnColumn cols="2">
              <ReadOnlyText label="Last Name" :value="txStore.contact?.lastName" :showLoading="pageState.state.loading" />
            </MnColumn>
          </MnRow>
          <!-- Display user's address and phone number in a read-only format. -->
          <MnRow>
            <MnColumn cols="4">
              <ReadOnlyText label="Address" :value="txStore.contact?.fullAddress" :showLoading="pageState.state.loading" />
            </MnColumn>
            <MnColumn cols="2">
              <ReadOnlyText label="Phone" :value="txStore.contact?.phone" :showLoading="pageState.state.loading" />
            </MnColumn>
          </MnRow>
          <!-- Display user's email and organization in a read-only format. -->
          <MnRow>
            <MnColumn cols="4">
              <ReadOnlyText label="Email" :value="txStore.contact?.email" :showLoading="pageState.state.loading" />
            </MnColumn>
            <MnColumn cols="4">
              <ReadOnlyText label="Organization" :value="txStore.contact?.primaryOrganizationName" :showLoading="pageState.state.loading" />
            </MnColumn>
          </MnRow>
        </div>
      </template>
    </CardSection>
    <CardSection>
      <template #title>
        <!-- Title section for the transactions list. -->
        <h2 class="mb-0 fs-3">HEALTH CARE ENTITY TRANSACTIONS</h2>
        <p class="instructions">Below are listing of active and completed transactions.</p>
      </template>
      <template #content>
        <div class="p-2">
          <!-- Button to create a new transaction. -->
          <div class="d-flex mb-3">
            <PvButton label="Create Transaction" 
                      rounded
                      size="small"
                      severity="primary"
                      @click="addTransaction()"
                      :icon="ICONS.Add"/>
          </div>
          <!-- Tabs to switch between active and completed transactions. -->
          <PvTabs value="0">
            <PvTabList>
              <PvTab value="0">Active Transactions</PvTab>
              <PvTab value="1">Completed Transactions</PvTab>
            </PvTabList>
            <PvTabPanels>
              <PvTabPanel value="0">
                <!-- Component to display active transactions. -->
                <HceHomeTransactions :transactions="activeTransactions" />
              </PvTabPanel>
              <PvTabPanel value="1">
                <!-- Component to display completed transactions. -->
                <HceHomeTransactions :transactions="completedTransactions" :isTransactionsCompleted="true" />
              </PvTabPanel>
            </PvTabPanels>
          </PvTabs>
        </div>
      </template>
    </CardSection>

    <!-- Dialog for editing the user's profile. -->
    <PvDialog id="contact-dialog" v-model:visible="contactModalVisible" modal :style="{ width: '75%' }" :closable="false">
        <template #header>
          <h2>Edit Profile</h2>
        </template>
        <EditProfile ref="addContactRef"
                    :contactId="authStore.user.contactId"
                    :organizationId="authStore.user.primaryOrganizationId"
                    @data-saved="closeContactModal(true)"
                    @data-canceled="closeContactModal(false)" />
        <template #footer>
          <div class="d-flex justify-content-end pt-2">
            <PvButton type="button" class="me-2" rounded @click="onContactCanceled" title="Cancel change and close dialog." label="Cancel" severity="secondary" />
            <PvButton type="button" rounded raised @click="onContactSaved" title="Save profile changes." label="Save Profile" />
          </div>
        </template>
      </PvDialog>

      <!-- Dialog for adding a new transaction. -->
      <HceAddTransactionDialog v-model:visible="addTransactionVisible" @transactionSaved="handleTransactionSaved" />
  </div>
</template>

<script setup>
// ===============================
// IMPORTS
// ===============================
// Vue Core
import { ref, onMounted, onUnmounted } from 'vue';
import { useRouter } from 'vue-router';

// Components
import CardSection from '@/components/common/CardSection.vue';
import ReadOnlyText from '@components/common/ReadOnlyText.vue';
import MnLoader from '../common/MnLoader.vue';
import MnRow from '@/components/common/MnRow.vue';
import MnColumn from '@/components/common/MnColumn.vue';
import ErrorMessage from '../common/ErrorMessage.vue';
import EditProfile from '../organization/EditProfile.vue';
import HceHomeTransactions from './HceHomeTransactions.vue';
import HceAddTransactionDialog from '@/modules/hcet/components/HceAddTransactionDialog.vue';

// Composables
import { usePageState } from '@/composables/pageState';

// Stores
import { useAuthStore } from '@/stores/authStore';
import { useHceHomeTransactionsStore } from '@/modules/hcet/stores/HceHomeTransactionsStore';

// Constants and Utils
import { ICONS } from '@/utils/constants';
import { computed } from 'vue';

// ===============================
// PROPS & EMITS
// ===============================


// ===============================
// COMPOSABLES & STORES
// ===============================
const authStore = useAuthStore();
const router = useRouter();
const pageState = usePageState();
const txStore = useHceHomeTransactionsStore();

// ===============================
// REACTIVE STATE
// ===============================
const contactModalVisible = ref(false);
const addContactRef = ref(null);
const addTransactionVisible = ref(false);
// const addTransactionRef = ref(null);

// ===============================
// CONSTANTS
// ===============================


// ===============================
// COMPUTED PROPERTIES
// ===============================
const activeTransactions = computed(() => {
  return txStore.transactions.filter((t) => t.isActive);
});

const completedTransactions = computed(() => {
  return txStore.transactions.filter((t) => !t.isActive);
});

// ===============================
// METHODS
// ===============================
// Function to close the contact modal and refresh data if changes were saved.
const closeContactModal = async (dataSaved) => {
  contactModalVisible.value = false;
  if (dataSaved) {
    pageState.showSuccessToast('Contact saved successfully.');
    txStore.refreshContact();
  }
};

// Function to open the add transaction modal.
const addTransaction = () => {
  addTransactionVisible.value = true;
};

// Function to handle canceling profile edits.
const onContactCanceled = () => {
  contactModalVisible.value = false;
};

// Function to save profile edits.
const onContactSaved = () => {
  addContactRef.value?.onSubmit();
};

// Function to fetch and filter transactions into active and completed categories.
const fetchTransactions = async () => {
  await txStore.refreshTransactions();
};

// Function to handle saving a new transaction and navigating to its edit page.
const handleTransactionSaved = async (transactionId) => {
  addTransactionVisible.value = false;
  if (transactionId) {
    await fetchTransactions();
    router.push({ name: 'edittransaction', params: { id: transactionId } });
  }
};

// Function to trigger the save action for a new transaction.
// const handleTransactionSave = () => {
//   addTransactionRef.value.onSubmit();
// };

// Function to initialize the page by fetching contact and transaction data.
const initializePage = async () => {
  await txStore.fetchContactAndTransactions(authStore.user.contactId);
};

// ===============================
// WATCHERS
// ===============================


// ===============================
// LIFECYCLE HOOKS
// ===============================
onMounted(async () => {
  const intervalId = setInterval(() => {
    fetchTransactions();
  }, 300000);

  onUnmounted(() => {
    clearInterval(intervalId);
  });

  await pageState.fetch(() => initializePage());
});

// ===============================
// EXPOSE
// ===============================


</script>
